import { mapState } from "vuex";

import boxComponent from "@/components/child/containers/box.vue";
import portletComponent from "@/components/child/containers/portlet.vue";
import checkboxComponent from "@/components/child/form/checkbox.vue";
import comboComponent from "@/components/child/form/combo.vue";
import dateTimePickerComponent from "@/components/child/form/datetimepicker.vue";
import multiSelectComponent from "@/components/child/multiSelect/multiSelect.vue";
import { Component } from "@/decorators";
import DashboardModel from "@/models/dashboard/dashboardModel";
import Graficos from "@/models/enum/dashboard/graficos";
import UsuarioModel from "@/models/usuarioModel";
import franquiaService from "@/services/franquiaService";
import tipoVendaService from "@/services/tipoVendaService";
import { AppState, SessionActions } from "@/store/store";
import Bus from "@/utils/events/bus";

import DashboardComponent from "./dashboardComponent";
import periodoFiltroComponent from "./filtros/periodoFiltroComponent.vue";

// Precisa ser assim se não ocorre erro
// eslint-disable-next-line @typescript-eslint/no-var-requires
const echarts = require("echarts");

import "./dashboardComponent.scss";

@Component({
    components: {
        boxComponent,
        checkboxComponent,
        portletComponent,
        dateTimePickerComponent,
        multiSelectComponent,
        comboComponent,
        periodoFiltroComponent,
    },
    computed: {
        ...mapState({
            usuarioLogado: (state: AppState) => state.session.usuarioLogado,
        }),
    },
})
export default class EvolucaoVendas extends DashboardComponent {
    // State computed props
    usuarioLogado: UsuarioModel;

    private totalVendas = 0;
    private totalQtdPedidos = 0;
    private metaMes = 0;

    private graficoVendasDiario: any;
    private graficoSomaVendasPeriodoAprovadas: any;
    private graficoQtdeVendasPeriodoAprovadas: any;
    private graficoSomaVendasPeriodoRejeitadas: any;
    private graficoQtdeVendasPeriodoRejeitadas: any;
    private graficoQtdeAtendimentos: any;
    private graficoTicketMedio: any;
    private graficoOrcamentosRejeitados: any;

    franquiaIds: Array<Object> = [];
    defaultFranquia: Array<Object> = [];
    franquiasOptions: Array<Object> = [];
    tipoVendaOptions: Array<Object> = [];
    taxaEntrega = true;
    tipoVenda = 0;

    private graphStyle = {
        width: "100%",
        height: "300px",
        margin: "25px 0px 0px 0px",
        // @ts-ignore
        zoom: this.compensarZoomGrafico(document.body.style.zoom),
    };

    private async mounted() {
        await this.$store.dispatch(SessionActions.LOAD_USUARIO_LOGADO);
        Bus.$emit("hide-nav-menu", false);

        await this.loadFranquias().withLoading();
        this.tipoVendaOptions = await tipoVendaService.comboCached(this.usuarioLogado.franquiaId);
        this.defaultFranquia.push(this.franquiasOptions.find(p => p["value"] === this.usuarioLogado.franquiaId));
    }

    private loadReferences() {
        this.graficoVendasDiario = echarts.init(this.$refs.vendasDiario);
        this.graficoSomaVendasPeriodoAprovadas = echarts.init(this.$refs.somaVendasPeriodoAprovadas);
        this.graficoQtdeVendasPeriodoAprovadas = echarts.init(this.$refs.qtdeVendasPeriodoAprovadas);
        this.graficoSomaVendasPeriodoRejeitadas = echarts.init(this.$refs.somaVendasPeriodoRejeitadas);
        this.graficoQtdeVendasPeriodoRejeitadas = echarts.init(this.$refs.qtdeVendasPeriodoRejeitadas);
        this.graficoQtdeAtendimentos = echarts.init(this.$refs.qtdeAtendimentos);
        this.graficoTicketMedio = echarts.init(this.$refs.ticketMedio);
        this.graficoOrcamentosRejeitados = echarts.init(this.$refs.orcamentosRejeitados);

        this.setGraficos([
            this.graficoVendasDiario,
            this.graficoSomaVendasPeriodoAprovadas,
            this.graficoQtdeVendasPeriodoAprovadas,
            this.graficoSomaVendasPeriodoRejeitadas,
            this.graficoQtdeVendasPeriodoRejeitadas,
            this.graficoQtdeAtendimentos,
            this.graficoTicketMedio,
            this.graficoOrcamentosRejeitados,
        ]);
    }

    private async onUpdateDashboard(dataInicial: Date, dataFinal: Date) {
        this.loadReferences();
        this.showLoadingGraficos();

        const data = await this.service
            .getEvolucaoVendas(
                this.franquiaIds.map(p => p["value"]),
                dataInicial,
                dataFinal,
                this.taxaEntrega,
                this.tipoVenda,
            )
            .withLoading()
            .resolveWithJSON<{
                cardsData: {
                    valorVendasMes?: number;
                    totalPedidosMes?: number;
                    metaMes?: number;
                };
                informacoes: Map<Graficos, DashboardModel[]>;
            }>();

        this.totalVendas = data.cardsData.valorVendasMes != null ? data.cardsData.valorVendasMes : 0;
        this.totalQtdPedidos = data.cardsData.totalPedidosMes != null ? data.cardsData.totalPedidosMes : 0;
        this.metaMes = data.cardsData.metaMes != null ? data.cardsData.metaMes : 0;

        this.graficoVendasDiario.setOption(
            this.getOpcoesGrafico({
                titulo: this.$t("__.ts.vendasDiario") as string,
                labels: data.informacoes["VendasDiario"].map(d => `${d.label}h`),
                dados: data.informacoes["VendasDiario"].map(d => d.valor.toFixed(2)),
                tipoGrafico: "line",
                format: "<b>{b}</b><br/>R$ {c}",
                nomeSerie: "Valor",
            }),
        );

        this.graficoSomaVendasPeriodoAprovadas.setOption(
            this.getOpcoesGrafico({
                titulo: this.$t("__.ts.somaVendasPeriodoAprovadas") as string,
                labels: data.informacoes["SomaVendasPeriodoAprovadas"].map(d => this.formatLabel(d.label)),
                dados: data.informacoes["SomaVendasPeriodoAprovadas"].map(d => d.valor.toFixed(2)),
                tipoGrafico: "line",
                format: "<b>{b}</b><br/>R$ {c}",
                nomeSerie: data.informacoes["SomaVendasPeriodoAprovadas"].map(d => d.serie)[0],
            }),
        );

        this.graficoQtdeVendasPeriodoAprovadas.setOption(
            this.getOpcoesGrafico({
                titulo: this.$t("__.ts.qtdeVendasPeriodoAprovadas") as string,
                labels: data.informacoes["QtdeVendasPeriodoAprovadas"].map(d => this.formatLabel(d.label)),
                dados: data.informacoes["QtdeVendasPeriodoAprovadas"].map(d => d.valor),
                tipoGrafico: "line",
                format: "<b>{b}</b><br/>{c}",
                nomeSerie: data.informacoes["QtdeVendasPeriodoAprovadas"].map(d => d.serie)[0],
            }),
        );

        this.graficoSomaVendasPeriodoRejeitadas.setOption(
            this.getOpcoesGrafico({
                titulo: this.$t("__.ts.somaVendasPeriodoRejeitadas") as string,
                labels: data.informacoes["SomaVendasPeriodoRejeitadas"].map(d => this.formatLabel(d.label)),
                dados: data.informacoes["SomaVendasPeriodoRejeitadas"].map(d => d.valor.toFixed(2)),
                tipoGrafico: "line",
                format: "<b>{b}</b><br/>R$ {c}",
                cor: "rgb(212,7,7)",
                nomeSerie: data.informacoes["SomaVendasPeriodoRejeitadas"].map(d => d.serie)[0],
            }),
        );

        this.graficoQtdeVendasPeriodoRejeitadas.setOption(
            this.getOpcoesGrafico({
                titulo: this.$t("__.ts.qtdeVendasPeriodoRejeitadas") as string,
                labels: data.informacoes["QtdeVendasPeriodoRejeitadas"].map(d => this.formatLabel(d.label)),
                dados: data.informacoes["QtdeVendasPeriodoRejeitadas"].map(d => d.valor),
                tipoGrafico: "line",
                format: "<b>{b}</b><br/>{c}",
                cor: "rgb(212,7,7)",
                nomeSerie: data.informacoes["QtdeVendasPeriodoRejeitadas"].map(d => d.serie)[0],
            }),
        );

        this.graficoOrcamentosRejeitados.setOption(
            this.getOpcoesGrafico({
                titulo: this.$t("__.ts.qtdeOrcamentosRejeitados") as string,
                labels: data.informacoes["OrcamentosRejeitados"].map(d => this.formatLabel(d.label)),
                dados: data.informacoes["OrcamentosRejeitados"].map(d => d.valor),
                tipoGrafico: "line",
                format: "<b>{b}</b><br/>{c}",
                cor: "rgb(212,7,7)",
                nomeSerie: data.informacoes["OrcamentosRejeitados"].map(d => d.serie)[0],
            }),
        );

        this.graficoQtdeAtendimentos.setOption(
            this.getOpcoesGrafico({
                titulo: this.$t("__.ts.qtdeAtendimentos") as string,
                labels: data.informacoes["QtdeAtendimentos"].map(d => this.formatLabel(d.label)),
                dados: data.informacoes["QtdeAtendimentos"].map(d => d.valor),
                tipoGrafico: "line",
                format: "<b>{b}</b><br/>{c}",
                nomeSerie: data.informacoes["QtdeAtendimentos"].map(d => d.serie)[0],
            }),
        );

        this.graficoTicketMedio.setOption(
            this.getOpcoesGrafico({
                titulo: this.$t("__.ts.ticketMedio") as string,
                labels: data.informacoes["TicketMedio"].map(d => this.formatLabel(d.label)),
                dados: data.informacoes["TicketMedio"].map(d => d.valor.toFixed(2)),
                tipoGrafico: "line",
                format: "<b>{b}</b><br/>R$ {c}",
                nomeSerie: data.informacoes["TicketMedio"].map(d => d.serie)[0],
            }),
        );

        this.hideLoadingGraficos();
    }

    private formatLabel(label: string): string {
        if (label.includes("/") && label.length > 7) {
            return label;
        }
        return this.getMonthName(label);
    }

    private async loadFranquias() {
        const franquias = await franquiaService.comboCached(this.usuarioLogado.franquiaId);
        this.franquiasOptions = franquias.map(franquia => ({
            value: franquia.id,
            text: franquia.nomeFantasia,
        }));
    }

    protected convertToString(numero: number) {
        return numero == null ? "0" : numero.toString();
    }

    private onSelecionarTodas() {
        this.franquiaIds = this.franquiasOptions;
        this.defaultFranquia = this.franquiasOptions;
    }

    private onLimpar() {
        this.franquiaIds = [];
        this.defaultFranquia = [];
    }
}
